/* Copyright (C) Okahu Inc 2023-2024. All rights reserved. */
'use client';

import React, { Dispatch, SetStateAction, useEffect, useState } from 'react';
import { useLanguage } from '@/providers/LanguageProvider';
import { Service } from 'types/services';

import { useComponents } from '@/hooks/useComponents';
import { DetailsPageSpinner } from '@/components/spinner/details-page-spinner';

import { Input } from '../../ux-common/src/components/ui/input';
import { System } from '../icons';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '../ui/command';
import { Popover, PopoverContent, PopoverTrigger } from '../ui/popover';
import { ScrollArea } from '../ui/scroll-area';

export const ServiceDropdown = ({
  selectedService,
  onFieldChange,
  isDisabled,
}: {
  selectedService: Service | null;
  onFieldChange: Dispatch<SetStateAction<Service | null>>;
  isDisabled?: boolean;
}) => {
  const [isPopoverOpen, setIsPopoverOpen] = useState(false);
  const [serviceLoading, setServiceLoading] = useState(false);

  const { messages } = useLanguage();
  const generalIntl = messages?.General;

  const {
    state: { activeServices },
    componentsActions,
  } = useComponents();

  const fetchActiveService = async () => {
    try {
      setServiceLoading(true);
      await componentsActions.fetchActiveServices();
    } catch (error) {
      console.log(error);
    } finally {
      setServiceLoading(false);
    }
  };

  useEffect(() => {
    fetchActiveService();
  }, []);

  const handleServiceSelect = (serviceSelected: Service) => {
    onFieldChange(serviceSelected);
    setIsPopoverOpen(false);
  };

  return (
    <div className="flex flex-col gap-1">
      <span className="text-form-input-text text-form-input-rest">
        {generalIntl?.service_associated}{' '}
      </span>
      <Popover open={isPopoverOpen} onOpenChange={setIsPopoverOpen}>
        <PopoverTrigger asChild>
          <div className="relative">
            <Input
              placeholder={
                serviceLoading
                  ? generalIntl?.loading
                  : generalIntl?.choose_services_to_be_associated
              }
              className="h-[42px] cursor-pointer rounded-sm border-form-control-border-rest pr-10"
              value={selectedService?.display_name}
              readOnly
              disabled={isDisabled}
            />
            <div className="pointer-events-none absolute inset-y-0 right-3 flex items-center">
              {System.ChevronDownIcon('size-5 text-form-input-disabled')}
            </div>
          </div>
        </PopoverTrigger>

        <PopoverContent
          sideOffset={0}
          className="z-[1000] w-64 space-y-2 bg-white p-0"
          align="start"
        >
          <Command className="rounded-sm rounded-b-none bg-white">
            <CommandInput
              placeholder={`${generalIntl?.search}...`}
              iconColor="text-form-input-disabled"
              className="text-[#323130] placeholder:text-[#8C9EA1]"
            />
            {serviceLoading ? (
              <div className="min-h-44 p-4 text-black">
                <DetailsPageSpinner />
              </div>
            ) : (
              <CommandList className="bg-white">
                <CommandEmpty className="h-40 pt-4 text-center text-[#323130]">
                  {messages?.PromptFilters?.multi_select?.no_results_found}
                </CommandEmpty>
                <CommandGroup className="text-[#323130]">
                  <ScrollArea className="h-40">
                    {activeServices?.map((service: Service) => (
                      <CommandItem
                        key={service.component_name}
                        onSelect={() => handleServiceSelect(service)}
                        className="cursor-pointer data-[selected=true]:bg-[#EDEBE9] data-[selected=true]:text-[#323130]"
                      >
                        <div className="cursor-pointer rounded-md px-2 py-1 hover:bg-gray-100">
                          {service.display_name}
                        </div>
                      </CommandItem>
                    ))}
                  </ScrollArea>
                </CommandGroup>
              </CommandList>
            )}
          </Command>
        </PopoverContent>
      </Popover>
    </div>
  );
};
